import { type ReactNode, useEffect } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ChevronRight, Download, RefreshCw, Send, Sparkles } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Alert } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  type Attribution,
  type CrossEchelon,
  CrossEchelonStrip,
  deltaTone,
  directionLabel,
  DriverBadge,
  type DriverRow,
  fmtConfidence,
  fmtDateTime,
  fmtInt,
  fmtPct,
  fmtSignedPct,
  fmtIdr,
  LabelBadge,
  type Narration,
  NARRATION_ACTIVE,
  NarratedProse,
  NarrationStatusBadge,
  narrationSourceLabels,
  periodLabel,
  SourceRefs,
  TierBadge,
  tierLabel,
} from "./shared"

type Props = {
  driver: DriverRow
  attributions: Attribution[]
  cross_echelon: CrossEchelon | null
  breadcrumb: { index_path: string }
  narration: Narration | null
  narration_enabled: boolean
  narration_supported: boolean
  can_generate: boolean
  can_publish: boolean
}

// Human labels for the structured evidence metrics shown under each driver.
const METRIC_LABELS: Record<string, string> = {
  delta_mom_pct: "Δ MoM",
  delta_value: "Δ nilai",
  sell_out_delta: "Offtake distributor",
  sell_out_source: "Sumber sell-out",
  stock_delta: "Δ stok",
  days_of_cover: "DOI (hari)",
  price_mix_effect: "Efek harga",
  volume_effect: "Efek volume",
  own_price_delta_pct: "Δ harga shelf own",
  max_disc_pct: "Diskon maks",
  gap_pct: "Gap harga",
  share_delta_pp: "Δ share (pp)",
  product_count: "Jumlah produk",
  activity_types: "Jenis aktivitas",
  directive_types: "Jenis direktif",
  domains: "Domain sinyal",
  count: "Jumlah",
  source: "Sumber",
}

function fmtMetric(key: string, value: unknown): string {
  if (value == null) return "—"
  if (Array.isArray(value)) return value.join(", ")
  if (typeof value === "number") {
    if (key === "max_disc_pct") return `${Math.round(value * 100)}%`
    if (key.endsWith("_pct")) return `${value.toFixed(1)}%`
    if (key.endsWith("_pp")) return `${value.toFixed(2)} pp`
    if (key === "days_of_cover") return `${Math.round(value)}`
    return new Intl.NumberFormat("id-ID").format(value)
  }
  return String(value)
}

export default function Show({
  driver,
  attributions,
  cross_echelon,
  breadcrumb,
  narration,
  narration_enabled,
  narration_supported,
  can_generate,
  can_publish,
}: Props) {
  const title = `${driver.product} → ${driver.party}`
  const description = `Kenapa sell-in ${driver.product} ke ${driver.party} (eselon ${driver.tier}) bergerak ${fmtSignedPct(
    driver.delta_mom_pct,
  )} pada ${periodLabel(driver.period)} — pendorong ter-ranking dan ter-citasi (bukti, bukan sebab).`

  return (
    <>
      <Head title={`${title} — Driver Sell-In`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${title} — Driver Sell-In`} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell width="wide">
        <nav className="mb-2 flex flex-wrap items-center gap-1.5 text-xs text-ink-muted" aria-label="Breadcrumb">
          <Link href={breadcrumb.index_path} className="hover:text-ink-body">
            Driver Sell-In
          </Link>
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
          <span className="text-ink-body">{periodLabel(driver.period)}</span>
        </nav>

        {/* Header */}
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>{driver.product}</h1>
              <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
                <TierBadge tier={driver.tier} />
                <span>
                  {driver.party} · {periodLabel(driver.period)}
                </span>
                <span className="text-xs">({tierLabel(driver.tier)})</span>
              </p>
            </div>
            <div className="flex flex-col items-start gap-1 sm:items-end">
              <Badge tone={deltaTone(driver.delta_mom_pct)}>Sell-in {fmtSignedPct(driver.delta_mom_pct)} MoM</Badge>
              <span className="text-xs text-ink-muted">YoY {fmtSignedPct(driver.delta_yoy_pct)}</span>
            </div>
          </div>
        </div>

        {/* DQ banner */}
        {driver.dq_flags.length > 0 && (
          <div className="mt-6 rounded-lg border border-hairline bg-surface p-4">
            <div className="flex items-center gap-2">
              <Badge tone="signal">Kualitas data</Badge>
              <span className="text-sm text-ink-body">
                Sebagian sinyal pada grain ini tipis — angka di bawah hanya dari leg yang tersedia.
              </span>
            </div>
            <ul className="mt-2 flex flex-wrap gap-2">
              {driver.dq_flags.map((flag, i) => (
                <li key={`${flag}-${i}`}>
                  <Badge tone="muted">{flag}</Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* The "Why" — ranked, cited driver attributions */}
        <section className="mt-8">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="mb-0 text-sm font-medium text-ink-display">Kenapa bergerak — pendorong ter-ranking</h2>
            <Badge tone={attributions.some((a) => a.label === "identified") ? "success" : "muted"}>
              {attributions.some((a) => a.label === "identified") ? "Driver teridentifikasi" : "Belum terjelaskan"}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-ink-muted">
            Pendorong yang hadir saat angka bergerak — bukti deterministik ter-citasi, bukan klaim sebab-akibat. Pergerakan sisi
            pasokan tidak pernah diatribusikan ke permintaan tanpa bukti.
          </p>

          {attributions.length === 0 ? (
            <p className="mt-4 text-sm text-ink-muted">Tidak ada pendorong untuk pergerakan ini.</p>
          ) : (
            <ol className="mt-4 space-y-3">
              {attributions.map((a) => (
                <AttributionCard key={a.rank} attribution={a} />
              ))}
            </ol>
          )}
        </section>

        {/* M5: the cross-echelon strip — where this product×month localizes (PS→SS→SO). */}
        {cross_echelon && <CrossEchelonStrip ce={cross_echelon} />}

        {/* Decomposition detail (the deterministic M3 numbers) */}
        <section className="mt-8">
          <h2 className="text-sm font-medium text-ink-display">Dekomposisi (deterministik)</h2>

          <h3 className="mt-3 text-xs font-medium uppercase tracking-wide text-ink-muted">Price-Volume-Mix (Δ nilai MoM)</h3>
          <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <MetricCard title="Nilai sekarang" value={fmtIdr(driver.net_value)} />
            <MetricCard title="Efek volume" value={fmtIdr(driver.volume_effect)} />
            <MetricCard title="Efek harga" value={fmtIdr(driver.price_mix_effect)} />
          </div>

          <h3 className="mt-5 text-xs font-medium uppercase tracking-wide text-ink-muted">
            Inventory bridge (sell-in ≈ sell-out + Δstok)
          </h3>
          <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard title="Δ stok" value={fmtInt(driver.stock_delta)} />
            <MetricCard title="Sell-out (bridge)" value={fmtInt(driver.sell_out_qty)} note={driver.sell_out_source ?? undefined} />
            <MetricCard title="Offtake distributor (MoM)" value={fmtSignedPct(driver.sell_out_delta)} note="Konteks, bukan term bridge" />
            <MetricCard title="Residual bridge" value={fmtInt(driver.bridge_residual)} note="≈ 0 = rekonsiliasi" />
          </div>

          <h3 className="mt-5 text-xs font-medium uppercase tracking-wide text-ink-muted">Konteks</h3>
          <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard title="DOI (hari)" value={driver.days_of_cover == null ? "—" : fmtInt(driver.days_of_cover)} />
            <MetricCard title="Sell-through" value={fmtPct(driver.sell_through_pct)} />
            <MetricCard title="Service level" value={fmtPct(driver.service_level_pct)} />
            <MetricCard title="Gap harga vs pasar" value={fmtSignedPct(driver.price_gap_pct)} />
          </div>
        </section>

        {/* AI "why" narration (M5) — rendered only when the kill-switch is on. */}
        <NarrationSection
          driverId={driver.id}
          narration={narration}
          enabled={narration_enabled}
          supported={narration_supported}
          tier={driver.tier}
          canGenerate={can_generate}
          canPublish={can_publish}
        />
      </AppShell>
    </>
  )
}

function AttributionCard({ attribution }: { attribution: Attribution }) {
  const ev = attribution.evidence ?? {}
  const metrics = ev.metrics ?? {}
  const metricKeys = Object.keys(metrics)
  return (
    <li className="rounded-lg border border-hairline bg-surface p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-display text-sm text-ink-muted tabular-nums">#{attribution.rank}</span>
        <DriverBadge type={attribution.driver_type} />
        {attribution.direction && <span className="text-xs text-ink-muted">{directionLabel(attribution.direction)}</span>}
        <LabelBadge label={attribution.label} />
        {attribution.confidence != null && (
          <span className="ml-auto text-xs text-ink-muted">Keyakinan {fmtConfidence(attribution.confidence)}</span>
        )}
      </div>
      {ev.summary && <p className="mt-2 text-sm text-ink-body">{ev.summary}</p>}
      {metricKeys.length > 0 && (
        <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-ink-muted">
          {metricKeys.map((k) => (
            <div key={k} className="flex items-baseline gap-1">
              <dt>{METRIC_LABELS[k] ?? k}:</dt>
              <dd className="font-medium text-ink-body">{fmtMetric(k, metrics[k])}</dd>
            </div>
          ))}
        </dl>
      )}
      <SourceRefs refs={ev.refs} />
    </li>
  )
}

function MetricCard({ title, value, note }: { title: string; value: ReactNode; note?: string }) {
  return (
    <div className="rounded-lg border border-hairline bg-surface p-4">
      <span className="text-xs text-ink-muted">{title}</span>
      <div className="mt-2 font-display text-xl text-ink-display tabular-nums">{value}</div>
      {note && <div className="mt-0.5 text-xs text-ink-muted">{note}</div>}
    </div>
  )
}

// ── M5: the AI "why" narration panel (kill-switched) ─────────────────────────
// Keyed to the driver grain (the :id), so generate/publish/export need no period
// query. Off ⇒ renders null: the deterministic M4 panel above is untouched.
function NarrationSection({
  driverId,
  narration,
  enabled,
  supported,
  tier,
  canGenerate,
  canPublish,
}: {
  driverId: number
  narration: Narration | null
  enabled: boolean
  supported: boolean
  tier: string
  canGenerate: boolean
  canPublish: boolean
}) {
  const active = narration ? NARRATION_ACTIVE.includes(narration.status) : false

  // Poll while the background narration runs; stop as soon as it lands.
  // useEffect ⇒ client-only (SSR-safe).
  useEffect(() => {
    if (!active) return
    const timer = setInterval(
      () => router.reload({ only: ["narration", "can_publish", "can_generate"] }),
      3500,
    )
    return () => clearInterval(timer)
  }, [active])

  // Kill-switch off ⇒ clean no-op: the deterministic M4 panel is unaffected.
  if (!enabled) return null

  // PS rows are keyed by SAP counterparty (no account) → not narratable. The strip +
  // deterministic panel explain them; show a short note rather than a generate button.
  if (!supported) {
    return (
      <section className="mt-8 rounded-lg border border-hairline bg-surface p-5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-ink-muted" aria-hidden="true" />
          <h2 className="mb-0 text-sm font-medium text-ink-display">Narasi “Kenapa” Sell-In (AI)</h2>
        </div>
        <p className="mt-1 text-sm text-ink-muted">
          Narasi AI tersedia untuk eselon SS (akun). Eselon {tier} (counterparty) dijelaskan oleh strip lintas-eselon dan
          dekomposisi deterministik di atas.
        </p>
      </section>
    )
  }

  const base = `/admin/sell-in/drivers/${driverId}`
  const generate = () => router.post(`${base}/narrate`, {}, { preserveScroll: true })
  const publish = () => {
    if (window.confirm("Publikasikan narasi ini? Setelah terbit tidak bisa diubah.")) {
      router.patch(`${base}/publish`, {}, { preserveScroll: true })
    }
  }

  const unknownTokens = narration?.validation?.unknown_tokens ?? []
  const tokens = narration ? narration.input_tokens + narration.output_tokens : 0

  return (
    <section className="mt-8 rounded-lg border border-hairline bg-surface p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-accent" aria-hidden="true" />
          <h2 className="mb-0 text-sm font-medium text-ink-display">Narasi “Kenapa” Sell-In (AI)</h2>
          {narration && <NarrationStatusBadge status={narration.status} />}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {narration?.md_body && (
            <a
              href={`${base}/export`}
              className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-surface px-3 py-1.5 text-sm text-ink-body hover:text-accent"
            >
              <Download className="h-4 w-4" />
              .md
            </a>
          )}
          {canGenerate && !active && (
            <Button variant="secondary" size="sm" className="gap-1.5" onClick={generate}>
              <RefreshCw className="h-4 w-4" />
              {narration ? "Generate ulang" : "Generate"}
            </Button>
          )}
          {canPublish && !active && (
            <Button variant="primary" size="sm" className="gap-1.5" onClick={publish}>
              <Send className="h-4 w-4" />
              Publikasikan
            </Button>
          )}
        </div>
      </div>

      <p className="mt-1 text-sm text-ink-muted">
        Penjelasan naratif dari dekomposisi deterministik — hanya angka yang ada di data (anti-halusinasi, pencarian web
        nonaktif, nama akun tidak disebut). Bukti ko-okuren, bukan klaim sebab-akibat.
      </p>

      {active ? (
        <p className="mt-4 text-sm text-ink-muted">Narasi sedang diproses — halaman diperbarui otomatis…</p>
      ) : narration?.status === "failed" ? (
        <Alert tone="danger" title="Narasi gagal" className="mt-4">
          {narration.error_message ?? "Terjadi kesalahan saat membuat narasi."}
        </Alert>
      ) : narration?.md_body ? (
        <>
          {narration.status === "needs_review" && (
            <Alert tone="warning" title="Perlu ditinjau" className="mt-4">
              Validator tidak bisa mencocokkan {unknownTokens.length} angka ke context pack
              {unknownTokens.length > 0 && (
                <>
                  {" "}
                  (<span className="font-medium">{unknownTokens.join(", ")}</span>)
                </>
              )}
              . Angka yang ditandai kuning perlu dikoreksi sebelum dipublikasikan.
            </Alert>
          )}
          <div className="mt-4">
            <NarratedProse text={narration.md_body} unknownTokens={unknownTokens} />
          </div>
          {narration.sources.length > 0 && (
            <p className="mt-3 text-xs text-ink-muted">Sumber: {narrationSourceLabels(narration.sources).join(" · ")}</p>
          )}
          {narration.generated_at && (
            <p className="mt-2 text-xs text-ink-muted">
              {narration.model && <>Model {narration.model} · </>}
              {tokens > 0 && <>{tokens.toLocaleString("id-ID")} token · ${narration.cost.toFixed(4)} · </>}
              Dibuat {fmtDateTime(narration.generated_at)}
              {narration.published_at && <> · Terbit {fmtDateTime(narration.published_at)}</>}
            </p>
          )}
        </>
      ) : (
        <p className="mt-4 text-sm text-ink-muted">
          Belum ada narasi untuk pergerakan ini.{canGenerate ? " Klik “Generate” untuk membuatnya." : ""}
        </p>
      )}
    </section>
  )
}
